import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { listenToHostPayout } from "../../redux/payoutsSlice";
import { motion } from "framer-motion";
import { Coins, Activity } from "lucide-react";
import { CircularProgress } from "@mui/material";

// 🔹 Helper: format large numbers (1.2K, 3.4M, etc.)
const formatNumber = (num: number = 0): string => {
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1) + "B";
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return num.toString();
};

// 🔸 Main euro symbol style
const euro = <span style={{ color: "#bf9b30", fontWeight: 700 }}>€</span>;

const Credits: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { credits, todayBalance, pendingBalance, paidOut, loading } = useSelector(
    (state: RootState) => state.payouts
  );
  const user = useSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    if (user?.userId) dispatch(listenToHostPayout(user.userId));
  }, [dispatch, user]);

  if (loading)
    return (
      <div style={styles.loader}>
        <CircularProgress sx={{ color: "#bf9b30" }} />
      </div>
    );

  const metrics = [
    { label: "Available Credits", value: credits, icon: <Coins size={22} /> },
    { label: "Today’s Balance", value: todayBalance, icon: <Activity size={22} /> },
    { label: "Pending Balance", value: pendingBalance, icon: <Activity size={22} /> },
    { label: "Total Paid Out", value: paidOut, icon: <Coins size={22} /> },
  ];

  return (
    <div style={styles.container}>
      <h2 style={styles.header}>Your Credits Summary</h2>
      <div style={styles.grid}>
        {metrics.map((m, idx) => (
          <motion.div
            key={idx}
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 200 }}
            style={styles.card}
          >
            <div style={styles.iconBox}>{m.icon}</div>
            <div>
              <h3 style={styles.value}>
                <span style={styles.euroSymbol}>€</span>
                {formatNumber(m.value || 0)}
              </h3>
              <p style={styles.label}>{m.label}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default Credits;

// ─── Styles ─────────────────────────────────────────────
const styles: { [k: string]: React.CSSProperties } = {
  container: {
    color: "#fff",
    padding: "24px",
    fontFamily: "'Poppins', sans-serif",
  },
  header: {
    textAlign: "center",
    color: "#bf9b30",
    textTransform: "uppercase",
    marginBottom: 24,
    letterSpacing: 1,
    fontWeight: 600,
  },
  loader: {
    height: "50vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 20,
  },
  card: {
    background: "linear-gradient(145deg, #0a0a0a, #111)",
    border: "1px solid rgba(191,155,48,0.3)",
    borderRadius: 16,
    padding: "20px",
    display: "flex",
    alignItems: "center",
    gap: 14,
    boxShadow: "0 3px 10px rgba(0,0,0,0.4)",
    cursor: "default",
  },
  iconBox: {
    background: "rgba(191,155,48,0.15)",
    borderRadius: "50%",
    width: 44,
    height: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#bf9b30",
  },
  value: {
    fontSize: 22,
    fontWeight: 700,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  euroSymbol: {
    color: "#bf9b30",
    fontWeight: 800,
    fontSize: 20,
    marginRight: 4,
  },
  label: { fontSize: 13, color: "#bbb" },
};
